import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { CheckPlaneIcon } from "@images/svgIcons/status/checkPlaneIcon"
import { ErrorPlaneIcon } from "@images/svgIcons/status/errorPlaneIcon"
import { boxStaffPermissions } from "@modules/boxStaff/utils/boxStaffUtils"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useImperativeHandle, useMemo, useState } from "react"

export type BoxStaffPermissionsModalRef = {
  open: () => void
} & React.ComponentProps<"div">

type Props = {
  ref: React.Ref<BoxStaffPermissionsModalRef>
  className?: string
}

export function BoxStaffPermissionsModal({ ref, className, ...props }: Props) {
  const [showModal, setShowModal] = useState(false)
  const language = useLanguage()

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  const permissions = useMemo(() => {
    return boxStaffPermissions()
  }, [language])

  return (
    <Dialog open={showModal} onOpenChange={() => setShowModal(false)}>
      <DialogContent className={cn("h-[90%] w-212 bg-neutral-50", className)} {...props} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            <Text fontStyle="TL" className="text-textDefault-heading">
              {word("box.staff.permissionsModal.header.modalTitle")}
            </Text>
          </DialogTitle>
        </DialogHeader>
        <div className="flex w-full flex-col overflow-y-auto pr-4">
          <div className="rounded-4xl bg-neutral-100 p-12">
            <div className="px- grid w-full grid-cols-15 items-center gap-4 rounded-full bg-black py-2">
              <div className="col-span-2 p-1"></div>
              <div className="col-span-3 p-0">
                <Text fontStyle="LM" className="text-textDefault-body">
                  {word("box.staff.permissionsModal.header.feature")}
                </Text>
              </div>
              <div className="col-span-2 flex justify-center">
                <Text fontStyle="LM" className="text-textDefault-body">
                  {word("box.staff.permissionsModal.header.owner")}
                </Text>
              </div>
              <div className="col-span-2 flex justify-center">
                <Text fontStyle="LM" className="text-textDefault-body">
                  {word("box.staff.permissionsModal.header.coOwner")}
                </Text>
              </div>
              <div className="col-span-2 flex justify-center">
                <Text fontStyle="LM" className="text-textDefault-body">
                  {word("box.staff.permissionsModal.header.headCoach")}
                </Text>
              </div>
              <div className="col-span-2 flex justify-center">
                <Text fontStyle="LM" className="text-textDefault-body">
                  {word("box.staff.permissionsModal.header.coach")}
                </Text>
              </div>
              <div className="col-span-2 flex justify-center p-1">
                <Text fontStyle="LM" className="text-textDefault-body">
                  {word("box.staff.permissionsModal.header.financial")}
                </Text>
              </div>
            </div>

            {permissions.map((permission) =>
              permission.features.map((feature) => (
                <div
                  key={feature.name}
                  className="grid w-full grid-cols-15 items-center gap-4 border-b border-neutral-200 py-4 last:border-b-0"
                >
                  <div className="col-span-2 p-1">
                    {permission.features.indexOf(feature) === 0 && (
                      <Text fontStyle="BS" className="text-textDefault-body">
                        {permission.category}
                      </Text>
                    )}
                  </div>
                  <div className="col-span-3 p-0">
                    <Text fontStyle="BM" className="text-textDefault-body">
                      {feature.name}
                    </Text>
                  </div>
                  <div className="col-span-2 flex items-center justify-center p-1">
                    {feature.owner ? (
                      <CheckPlaneIcon color="currentColor" size={24} className="text-textDefault-body" />
                    ) : (
                      <ErrorPlaneIcon color="currentColor" size={24} className="text-textDefault-body" />
                    )}
                  </div>
                  <div className="col-span-2 flex items-center justify-center p-1">
                    {feature.coOwner ? (
                      <CheckPlaneIcon color="currentColor" size={24} className="text-textDefault-body" />
                    ) : (
                      <ErrorPlaneIcon color="currentColor" size={24} className="text-textDefault-body" />
                    )}
                  </div>
                  <div className="col-span-2 flex items-center justify-center p-1">
                    {feature.headCoach ? (
                      <CheckPlaneIcon color="currentColor" size={24} className="text-textDefault-body" />
                    ) : (
                      <ErrorPlaneIcon color="currentColor" size={24} className="text-textDefault-body" />
                    )}
                  </div>
                  <div className="col-span-2 flex items-center justify-center p-1">
                    {feature.coach ? (
                      <CheckPlaneIcon color="currentColor" size={24} className="text-textDefault-body" />
                    ) : (
                      <ErrorPlaneIcon color="currentColor" size={24} className="text-textDefault-body" />
                    )}
                  </div>
                  <div className="col-span-2 flex items-center justify-center p-1">
                    {feature.financial ? (
                      <CheckPlaneIcon color="currentColor" size={24} className="text-textDefault-body" />
                    ) : (
                      <ErrorPlaneIcon color="currentColor" size={24} className="text-textDefault-body" />
                    )}
                  </div>
                </div>
              )),
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
